import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore, getDateRangeFromPreset, formatDateForAPI } from '@/stores/settings-store';
import { api } from '@/lib/api';

interface GSCKPIData {
  clicks: number;
  impressions: number;
  ctr: number; // percentage
  position: number;
  clicksChange: number;
  impressionsChange: number;
  ctrChange: number;
  positionChange: number;
}

interface GSCQueryData {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface GSCPageData {
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

/**
 * Fetch GSC KPI data (totals for clicks, impressions, CTR, position)
 */
export function useGSCKPIs() {
  const { activePropertyId, properties } = useAuthStore();
  const { dateRange } = useSettingsStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const siteUrl = activeProperty?.gscSite?.siteUrl;

  return useQuery({
    queryKey: ['gsc-kpis', siteUrl, dateRange],
    queryFn: async (): Promise<GSCKPIData | null> => {
      if (!siteUrl) {
        return null;
      }

      const days = parseInt(dateRange.replace('d', ''), 10);
      const { startDate, endDate } = getDateRangeFromPreset(dateRange);

      // Get previous period for comparison
      const prevEndDate = new Date(startDate);
      prevEndDate.setDate(prevEndDate.getDate() - 1);
      const prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevStartDate.getDate() - days + 1);

      try {
        const [currentData, previousData] = await Promise.all([
          api.google.runGSCQuery({
            siteUrl,
            startDate: formatDateForAPI(startDate),
            endDate: formatDateForAPI(endDate),
            rowLimit: 1,
          }),
          api.google.runGSCQuery({
            siteUrl,
            startDate: formatDateForAPI(prevStartDate),
            endDate: formatDateForAPI(prevEndDate),
            rowLimit: 1,
          }),
        ]);

        // GSC returns aggregated totals when no dimensions are specified
        const current = currentData?.rows?.[0];
        const previous = previousData?.rows?.[0];

        if (!current) {
          return null;
        }

        const calcChange = (curr: number, prev: number): number => {
          if (prev === 0) return curr > 0 ? 100 : 0;
          return ((curr - prev) / prev) * 100;
        };

        const clicks = current.clicks || 0;
        const impressions = current.impressions || 0;
        const ctr = (current.ctr || 0) * 100; // Convert to percentage
        const position = current.position || 0;

        const prevClicks = previous?.clicks || 0;
        const prevImpressions = previous?.impressions || 0;
        const prevCtr = (previous?.ctr || 0) * 100;
        const prevPosition = previous?.position || 0;

        return {
          clicks,
          impressions,
          ctr,
          position,
          clicksChange: calcChange(clicks, prevClicks),
          impressionsChange: calcChange(impressions, prevImpressions),
          ctrChange: calcChange(ctr, prevCtr),
          positionChange: calcChange(position, prevPosition),
        };
      } catch (error) {
        console.error('Failed to fetch GSC KPIs:', error);
        throw error;
      }
    },
    enabled: !!siteUrl,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch top search queries from GSC
 */
export function useGSCTopQueries(limit = 10) {
  const { activePropertyId, properties } = useAuthStore();
  const { dateRange } = useSettingsStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const siteUrl = activeProperty?.gscSite?.siteUrl;

  return useQuery({
    queryKey: ['gsc-top-queries', siteUrl, limit, dateRange],
    queryFn: async (): Promise<GSCQueryData[] | null> => {
      if (!siteUrl) {
        return null;
      }

      const { startDate, endDate } = getDateRangeFromPreset(dateRange);

      try {
        const response = await api.google.runGSCQuery({
          siteUrl,
          startDate: formatDateForAPI(startDate),
          endDate: formatDateForAPI(endDate),
          dimensions: ['query'],
          rowLimit: limit,
        });

        if (!response?.rows) {
          return [];
        }

        return response.rows.map((row: any) => ({
          query: row.keys[0] || '',
          clicks: row.clicks,
          impressions: row.impressions,
          ctr: row.ctr * 100, // Convert to percentage
          position: row.position,
        }));
      } catch (error) {
        console.error('Failed to fetch GSC top queries:', error);
        throw error;
      }
    },
    enabled: !!siteUrl,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch top pages from GSC
 */
export function useGSCTopPages(limit = 10) {
  const { activePropertyId, properties } = useAuthStore();
  const { dateRange } = useSettingsStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const siteUrl = activeProperty?.gscSite?.siteUrl;

  return useQuery({
    queryKey: ['gsc-top-pages', siteUrl, limit, dateRange],
    queryFn: async (): Promise<GSCPageData[] | null> => {
      if (!siteUrl) {
        return null;
      }

      const { startDate, endDate } = getDateRangeFromPreset(dateRange);

      try {
        const response = await api.google.runGSCQuery({
          siteUrl,
          startDate: formatDateForAPI(startDate),
          endDate: formatDateForAPI(endDate),
          dimensions: ['page'],
          rowLimit: limit,
        });

        if (!response?.rows) {
          return [];
        }

        return response.rows.map((row: any) => ({
          page: row.keys[0] || '',
          clicks: row.clicks,
          impressions: row.impressions,
          ctr: row.ctr * 100,
          position: row.position,
        }));
      } catch (error) {
        console.error('Failed to fetch GSC top pages:', error);
        throw error;
      }
    },
    enabled: !!siteUrl,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch search performance over time
 */
export function useGSCPerformanceOverTime() {
  const { activePropertyId, properties } = useAuthStore();
  const { dateRange } = useSettingsStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const siteUrl = activeProperty?.gscSite?.siteUrl;
  const days = parseInt(dateRange.replace('d', ''), 10);

  return useQuery({
    queryKey: ['gsc-performance-over-time', siteUrl, dateRange],
    queryFn: async () => {
      if (!siteUrl) {
        return null;
      }

      const { startDate, endDate } = getDateRangeFromPreset(dateRange);

      try {
        const response = await api.google.runGSCQuery({
          siteUrl,
          startDate: formatDateForAPI(startDate),
          endDate: formatDateForAPI(endDate),
          dimensions: ['date'],
          rowLimit: days,
        });

        if (!response?.rows) {
          return [];
        }

        return response.rows
          .map((row: any) => ({
            date: row.keys[0] || '',
            clicks: row.clicks,
            impressions: row.impressions,
            ctr: row.ctr * 100,
            position: row.position,
          }))
          .sort((a: any, b: any) => a.date.localeCompare(b.date));
      } catch (error) {
        console.error('Failed to fetch GSC performance over time:', error);
        throw error;
      }
    },
    enabled: !!siteUrl,
    staleTime: 1000 * 60 * 5,
  });
}
